import { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { BottomDock } from "@/components/BottomDock";
import { isDockHidden } from "@/lib/dockVisibility";

/**
 * MobileFrame — wraps each screen in a phone-sized canvas centered on the
 * dark gallery background. On small screens (real phones) the frame collapses
 * to fill the viewport. Renders a persistent BottomDock for primary navigation
 * (the dock hides itself on immersive routes like /, /auth, /scan, /analyze).
 *
 * The content wrapper conditionally reserves bottom space for the dock so
 * dock-hidden routes (DM thread, etc.) can use the full vertical viewport
 * without a phantom band at the bottom.
 */
export function MobileFrame({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const dockHidden = isDockHidden(pathname);
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "stretch",
        justifyContent: "center",
        background: "hsl(var(--ink))",
        backgroundImage: "radial-gradient(circle at 30% 20%, hsl(257 25% 22%), hsl(257 47% 10%) 70%)",
      }}
    >
      <div
        // The paper-bg class is applied here (not just on each page's outer
        // div) so the themed pattern (Trapper Keeper rainbow stripes, Velvet
        // Underground twilight, Kraft, Butter) extends through the iOS
        // safe-area zones at the top notch and bottom home-indicator. The
        // safe-area padding lives on an inner wrapper below — when it was
        // on THIS element, WebKit on iOS only painted background-image
        // inside the padding box, so the safe-area zone read as a flat
        // band of background-color with no theme stripes.
        className="paper-bg"
        style={{
          width: "100%",
          maxWidth: 430,
          minHeight: "100vh",
          // Use backgroundColor (not background shorthand) — the shorthand
          // clears background-image to none, which wiped the .paper-bg
          // class's theme pattern (Trapper Keeper stripes etc.). With
          // backgroundColor the class's background-image survives and
          // paints the themed pattern edge-to-edge.
          backgroundColor: "hsl(var(--paper))",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
          // Allow vertical scrolling when a page's content exceeds the viewport
          // (e.g. waitlist/signup on shorter phones). Keep horizontal clipped so
          // the analog overlays + rounded frame stay contained.
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        {/* Status-bar safe-area band — paints a dark theme-keyed shade
            in the iOS notch / Dynamic Island zone so the otherwise-empty
            gap above the page reads as intentional. position: fixed so
            it stays put while the page scrolls. width clamped to match
            the centered MobileFrame width on desktop. Hidden on web/
            Android where env(safe-area-inset-top) resolves to 0px. */}
        <div
          aria-hidden
          style={{
            position: "fixed",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "100%",
            maxWidth: 430,
            height: "env(safe-area-inset-top, 0px)",
            background: "hsl(var(--safe-area-bg))",
            zIndex: 40,
            pointerEvents: "none",
          }}
        />
        {/* Analog layer — paper grain + light leak + vignette. Sits behind
            page content (.analog-* are pointer-events: none). */}
        <div className="analog-grain" aria-hidden />
        <div className="analog-leak" aria-hidden />
        <div className="analog-vignette" aria-hidden />
        {/* Content wrapper holds the safe-area padding so the themed parent
            has no padding zone to fight with — its bg-image paints clean
            edge-to-edge. The dock's own safe-area handling lives in
            BottomDock so we only need bottom reservation for content. */}
        <div style={{
          position: "relative",
          zIndex: 5,
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingLeft: "env(safe-area-inset-left, 0px)",
          paddingRight: "env(safe-area-inset-right, 0px)",
          // Dock-visible routes: reserve 56px (dock content height: 8 +
          // 40 icon + 8) plus the iOS home-indicator inset so content
          // doesn't slide under the dock. Dock-hidden routes (DM thread,
          // splash, scan, etc.): only reserve the home-indicator inset,
          // so the page can use the full vertical viewport — without
          // this the DM composer sat ~56px below the bottom of the
          // visible screen and the user couldn't see it.
          paddingBottom: dockHidden
            ? "env(safe-area-inset-bottom, 0px)"
            : "calc(56px + env(safe-area-inset-bottom, 0px))",
        }}>
          {children}
        </div>
        <BottomDock />
      </div>
    </div>
  );
}
